import { FunctionComponent } from "react";
import { useAppSelector } from "./store/hooks";
import { UserProfile } from "./services/FirebaseUsers";

interface AchievementDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  check: (p: UserProfile) => boolean;
  reward: number; // монеты за первое получение
}

export const ALL_ACHIEVEMENTS: AchievementDef[] = [
  {
    id: "first_win",
    name: "Первая победа",
    description: "Выиграйте первую игру",
    icon: "◆",
    check: (p) => p.wins >= 1,
    reward: 1,
  },
  {
    id: "wins_10",
    name: "Десятка",
    description: "Одержите 10 побед",
    icon: "◈",
    check: (p) => p.wins >= 10,
    reward: 2,
  },
  {
    id: "wins_50",
    name: "Полусотня",
    description: "Одержите 50 побед",
    icon: "★",
    check: (p) => p.wins >= 50,
    reward: 5,
  },
  {
    id: "wins_100",
    name: "Сотня",
    description: "Одержите 100 побед",
    icon: "▲",
    check: (p) => p.wins >= 100,
    reward: 10,
  },
  {
    id: "streak_3",
    name: "На волне",
    description: "Выиграйте 3 игры подряд",
    icon: "▶",
    check: (p) => p.longestWinStreak >= 3,
    reward: 2,
  },
  {
    id: "streak_5",
    name: "Доминация",
    description: "Выиграйте 5 игр подряд",
    icon: "▶▶",
    check: (p) => p.longestWinStreak >= 5,
    reward: 3,
  },
  {
    id: "streak_10",
    name: "Непобедимый",
    description: "Выиграйте 10 игр подряд",
    icon: "◉",
    check: (p) => p.longestWinStreak >= 10,
    reward: 10,
  },
  {
    id: "gammon_1",
    name: "Гаммон",
    description: "Выиграйте гаммоном",
    icon: "◑",
    check: (p) => p.gammonsWon >= 1,
    reward: 2,
  },
  {
    id: "gammon_10",
    name: "Мастер гаммона",
    description: "Выиграйте 10 гаммонов",
    icon: "◓",
    check: (p) => p.gammonsWon >= 10,
    reward: 5,
  },
  {
    id: "backgammon_1",
    name: "Бэкгаммон",
    description: "Выиграйте бэкгаммоном",
    icon: "◕",
    check: (p) => p.backgammonsWon >= 1,
    reward: 3,
  },
  {
    id: "level_5",
    name: "Опытный",
    description: "Достигните 5 уровня",
    icon: "■",
    check: (p) => p.level >= 5,
    reward: 2,
  },
  {
    id: "level_10",
    name: "Профессионал",
    description: "Достигните 10 уровня",
    icon: "□■",
    check: (p) => p.level >= 10,
    reward: 5,
  },
  {
    id: "rating_1200",
    name: "Опытный игрок",
    description: "Наберите рейтинг 1200",
    icon: "▣",
    check: (p) => p.rating >= 1200,
    reward: 3,
  },
  {
    id: "rating_1500",
    name: "Умелый игрок",
    description: "Наберите рейтинг 1500",
    icon: "◈",
    check: (p) => p.rating >= 1500,
    reward: 10,
  },
  {
    id: "rating_1800",
    name: "Эксперт",
    description: "Наберите рейтинг 1800",
    icon: "★",
    check: (p) => p.rating >= 1800,
    reward: 50,
  },
  {
    id: "games_played_100",
    name: "Ветеран",
    description: "Сыграйте 100 игр",
    icon: "◎",
    check: (p) => p.gamesPlayed >= 100,
    reward: 5,
  },
  {
    id: "coins_5000",
    name: "Богач",
    description: "Накопите 5000 ₽",
    icon: "●",
    check: (p) => p.coins >= 5000,
    reward: 0,
  },
  {
    id: "coins_10000",
    name: "Миллионер",
    description: "Накопите 10000 ₽",
    icon: "◯",
    check: (p) => p.coins >= 10000,
    reward: 0,
  },
];

type AchievementsPanelProps = {
  onClose: () => void;
};

const AchievementsPanel: FunctionComponent<AchievementsPanelProps> = ({
  onClose,
}) => {
  const profile = useAppSelector((state) => state.user.profile);
  if (!profile) return null;

  const unlocked = new Set(profile.achievements ?? []);
  const total = ALL_ACHIEVEMENTS.length;
  const count = ALL_ACHIEVEMENTS.filter((a) => unlocked.has(a.id) || a.check(profile)).length;

  return (
    <div className="Ach-overlay" onClick={onClose}>
      <div className="Ach-modal" onClick={(e) => e.stopPropagation()}>
        <button className="Ach-close" onClick={onClose}>&#x2715;</button>

        <h2 className="Ach-title">Достижения</h2>

        <div className="Ach-progress-bar-wrap">
          <div className="Ach-progress-bar">
            <div
              className="Ach-progress-fill"
              style={{ width: `${(count / total) * 100}%` }}
            />
          </div>
          <span className="Ach-progress-label">{count} / {total}</span>
        </div>

        <div className="Ach-grid">
          {ALL_ACHIEVEMENTS.map((a) => {
            const done = unlocked.has(a.id) || a.check(profile);
            return (
              <div key={a.id} className={`Ach-card ${done ? "done" : "locked"}`}>
                <div className="Ach-card-icon">{done ? a.icon : "○"}</div>
                <div className="Ach-card-body">
                  <div className="Ach-card-name">{a.name}</div>
                  <div className="Ach-card-desc">{a.description}</div>
                  {a.reward > 0 && (
                    <div className="Ach-card-reward">+{a.reward} ₽</div>
                  )}
                </div>
                {done && <div className="Ach-card-check">✓</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AchievementsPanel;
